/**
 * Notifiche push (#126) — solo nell'app nativa.
 *
 * Chi crea riceve due notifiche per puzzle: quando l'altra persona lo risolve e
 * quando gli registra una reazione video. Le manda una Cloud Function
 * (functions/push.js) ai telefoni registrati in `pushTokens/{token}`.
 *
 * Il permesso si chiede una volta sola, dopo il primo puzzle condiviso, con una
 * schermata che spiega prima a cosa serve (PushOfferModal). Mai all'avvio.
 *
 * Il token si scrive solo dopo quel sì, sotto l'account in uso (anche anonimo:
 * i puzzle sono suoi lo stesso), e si riscrive quando cambia account, lingua o
 * token. All'uscita dall'account si cancella, così il telefono smette di
 * ricevere le notifiche di quell'account. Fino ad Android 12 il permesso c'è
 * già senza chiederlo: vale lo stesso il sì nella nostra schermata.
 */
import { Capacitor } from "@capacitor/core";
import { FirebaseMessaging } from "@capacitor-firebase/messaging";
import { Preferences } from "@capacitor/preferences";
import { doc, setDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { db, auth } from "./firebase";
import i18n from "../i18n";
import logger from "./logger";
import { isNativeApp } from "./platform";

export const PUSH_OFFER_KEY = "puzzly_push_offer_seen";
// Ha detto sì nella nostra schermata: senza, nessun token lascia il telefono.
export const PUSH_OPTIN_KEY = "puzzly_push_on";
// Il canale Android che compare nelle impostazioni del telefono. Lo stesso id
// sta nell'AndroidManifest come canale predefinito di FCM.
export const PUSH_CHANNEL_ID = "puzzle";
const LANGS = ["it", "en", "es", "fr"];
// Solo link di Firebase Storage: è lì che il player carica la reazione.
const STORAGE_URL = /^https:\/\/firebasestorage\.googleapis\.com\//;

let registeredToken = null;

function appLang() {
  const l = String(i18n.resolvedLanguage || i18n.language || "en").slice(0, 2);
  return LANGS.includes(l) ? l : "en";
}

/** "granted" | "denied" | "prompt" | "prompt-with-rationale" | "unsupported" */
export async function pushPermission() {
  if (!isNativeApp()) return "unsupported";
  try {
    const { receive } = await FirebaseMessaging.checkPermissions();
    return receive;
  } catch {
    return "unsupported";
  }
}

async function flag(key) {
  try {
    const { value } = await Preferences.get({ key });
    return value === "1";
  } catch {
    return null;
  }
}

/**
 * Mostrare adesso la schermata «Vuoi sapere quando lo risolve?»: solo
 * nell'app, se non l'ha mai vista e se il telefono non le ha già detto di no.
 */
export async function shouldOfferPush() {
  if (!isNativeApp()) return false;
  if ((await flag(PUSH_OFFER_KEY)) !== false) return false;
  const state = await pushPermission();
  return state === "prompt" || state === "prompt-with-rationale" || state === "granted";
}

export async function markPushOffered() {
  try { await Preferences.set({ key: PUSH_OFFER_KEY, value: "1" }); } catch { /* ignore */ }
}

// Mentre la proposta sta per aprirsi o è aperta, la richiesta di recensione
// (#124) non le si sovrappone: aspetta il prossimo ritorno nell'app.
let propostaAperta = false;
export function segnaPropostaPush(aperta) { propostaAperta = aperta; }
export function propostaPushAperta() { return propostaAperta; }

/**
 * Registra questo telefono sotto l'account in uso. Non chiede niente: senza il
 * permesso già dato, e senza il sì nella nostra schermata, non fa nulla.
 * @param {string} [token] quello appena arrivato con `tokenReceived`
 */
export async function registerPushToken(token) {
  if (!isNativeApp()) return null;
  const user = auth.currentUser;
  if (!user) return null;
  if (!(await flag(PUSH_OPTIN_KEY))) return null;
  if ((await pushPermission()) !== "granted") return null;
  const t = token || (await FirebaseMessaging.getToken()).token;
  if (!t) return null;
  await setDoc(doc(db, "pushTokens", t), {
    uid: user.uid,
    platform: Capacitor.getPlatform(),
    lang: appLang(),
    updatedAt: serverTimestamp(),
  });
  registeredToken = t;
  logger.info("Push: telefono registrato", { uid: user.uid });
  return t;
}

/**
 * Ha detto sì nella nostra schermata: ora la domanda del sistema, poi la
 * registrazione. Torna true se le notifiche sono accese.
 */
export async function enablePush() {
  await markPushOffered();
  if (!isNativeApp()) return false;
  const { receive } = await FirebaseMessaging.requestPermissions();
  if (receive !== "granted") {
    logger.info("Push: permesso non dato", { receive });
    return false;
  }
  try { await Preferences.set({ key: PUSH_OPTIN_KEY, value: "1" }); } catch { /* ignore */ }
  if (Capacitor.getPlatform() === "android") {
    try {
      await FirebaseMessaging.createChannel({
        id: PUSH_CHANNEL_ID,
        name: i18n.t("app.push.channelName"),
        importance: 4,
      });
    } catch (err) {
      logger.warn("Push: canale non creato", { error: err && err.message });
    }
  }
  await registerPushToken();
  return true;
}

/**
 * Prima di uscire dall'account: questo telefono smette di ricevere le sue
 * notifiche. Va chiamata da dentro l'account (le regole lasciano cancellare
 * solo i propri telefoni). Non blocca mai l'uscita.
 */
export async function unregisterPushToken() {
  if (!isNativeApp()) return;
  try {
    if (!(await flag(PUSH_OPTIN_KEY))) return;
    let t = registeredToken;
    if (!t && (await pushPermission()) === "granted") t = (await FirebaseMessaging.getToken()).token;
    registeredToken = null;
    if (t && auth.currentUser) await deleteDoc(doc(db, "pushTokens", t));
  } catch (err) {
    logger.warn("Push: token non cancellato all'uscita", { error: err && err.message });
  }
}

/**
 * Cosa fare quando si tocca una notifica: i dati che la Cloud Function mette
 * nel messaggio, ripuliti. null se non è una delle nostre.
 * @returns {{type: "solved"|"reaction", puzzleId: string, videoUrl: string|null}|null}
 */
export function notificationTarget(data) {
  if (!data || typeof data !== "object") return null;
  const { type, puzzleId, videoUrl } = data;
  if (type !== "solved" && type !== "reaction") return null;
  if (typeof puzzleId !== "string" || !/^[A-Za-z0-9]{1,64}$/.test(puzzleId)) return null;
  const video = type === "reaction" && typeof videoUrl === "string" && STORAGE_URL.test(videoUrl) ? videoUrl : null;
  return { type, puzzleId, videoUrl: video };
}

/**
 * Ascolta il token nuovo, il tocco su una notifica e, su Android, la notifica
 * che arriva ad app aperta: lì il sistema non la mostra (iOS sì, con il banner),
 * quindi la passa all'app perché la mostri lei. Un tocco che ha aperto l'app da
 * chiusa arriva anche se l'ascolto parte dopo (il plugin lo trattiene).
 * @param {{
 *   onOpen: (target: ReturnType<typeof notificationTarget>) => void,
 *   onForeground?: (n: {title: string, body: string, target: ReturnType<typeof notificationTarget>}) => void,
 * }} handlers
 * @returns {() => void} per smettere di ascoltare
 */
export function listenToPush({ onOpen, onForeground }) {
  if (!isNativeApp()) return () => {};
  const handles = [];
  let stopped = false;
  const keep = (p) => p.then((h) => { if (stopped) h.remove(); else handles.push(h); }).catch(() => {});

  keep(FirebaseMessaging.addListener("tokenReceived", ({ token }) => {
    registerPushToken(token).catch((err) => logger.warn("Push: token nuovo non salvato", { error: err && err.message }));
  }));
  keep(FirebaseMessaging.addListener("notificationActionPerformed", (event) => {
    const target = notificationTarget(event && event.notification && event.notification.data);
    logger.info("Push: notifica toccata", { type: target && target.type });
    if (target) onOpen(target);
  }));

  if (onForeground && Capacitor.getPlatform() === "android") {
    keep(FirebaseMessaging.addListener("notificationReceived", ({ notification } = {}) => {
      const target = notificationTarget(notification && notification.data);
      if (!target) return;
      onForeground({ title: notification.title || "", body: notification.body || "", target });
    }));
  }

  // La lingua viaggia con il token: chi cambia lingua riceve le notifiche nella nuova.
  const onLang = () => { if (registeredToken) registerPushToken(registeredToken).catch(() => {}); };
  i18n.on("languageChanged", onLang);

  return () => {
    stopped = true;
    handles.forEach((h) => h.remove());
    i18n.off("languageChanged", onLang);
  };
}
